const RESTORE_CONCURRENCY = 4;

/**
 * <lookbook-section>
 *
 * Restores load-more state and scroll position when a customer returns from a
 * lookbook post to the lookbook listing.
 *
 * Required dataset:
 *   data-grid-selector — CSS selector for the preview grid
 *   data-section-id    — Shopify section id for section_id rendering
 *   data-page-size     — Number of articles rendered per paginated page
 *
 * Required grid dataset:
 *   data-total-items — Total number of articles in the current paginated view
 */

class LookbookSection extends HTMLElement {
  #grid = null;
  #abortController = null;
  #boundOnClick = null;
  #boundOnLoadMoreAppended = null;
  #boundOnPageHide = null;
  #previousScrollRestoration = null;
  #storageKey = '';

  connectedCallback() {
    this.#grid = document.querySelector(this.dataset.gridSelector);
    this.#storageKey = `lookbook:${window.location.pathname}`;

    this.#boundOnClick = (event) => this.#onClick(event);
    this.#boundOnLoadMoreAppended = () => this.#saveState({ includeScroll: false });
    this.#boundOnPageHide = () => this.#saveState({ includeScroll: true });
    this.addEventListener('click', this.#boundOnClick);
    this.addEventListener('load-more:appended', this.#boundOnLoadMoreAppended);
    window.addEventListener('pagehide', this.#boundOnPageHide);

    this.#restoreState();
  }

  disconnectedCallback() {
    if (this.#boundOnClick) this.removeEventListener('click', this.#boundOnClick);
    if (this.#boundOnLoadMoreAppended) {
      this.removeEventListener('load-more:appended', this.#boundOnLoadMoreAppended);
    }
    if (this.#boundOnPageHide) window.removeEventListener('pagehide', this.#boundOnPageHide);
    this.#abortController?.abort();
    this.#releaseScrollRestoration();
  }

  #onClick(event) {
    if (!event.target.closest('[data-lookbook-preview-link]')) return;
    this.#saveState({ includeScroll: true });
  }

  #saveState({ includeScroll }) {
    const paginator = this.querySelector('load-more-paginator');
    const loaded = Number(paginator?.dataset.loaded || this.#grid?.children.length || 0);
    const pageSize = Number(this.dataset.pageSize || 0);
    const loadedPages = pageSize > 0 ? Math.max(1, Math.ceil(loaded / pageSize)) : 1;
    const currentState = this.#readState();

    try {
      sessionStorage.setItem(
        this.#storageKey,
        JSON.stringify({
          gridHtml: this.#getSnapshotGridHtml(),
          loaded,
          loadedPages,
          pageSize,
          totalItems: Number(this.#grid?.dataset.totalItems || 0),
          savedAt: Date.now(),
          scrollY: includeScroll ? window.scrollY : (currentState?.scrollY ?? 0),
          paginator: paginator
            ? {
                nextUrl: paginator.dataset.nextUrl ?? '',
                total: paginator.dataset.total ?? '',
                exhausted: paginator.hasAttribute('data-exhausted'),
              }
            : null,
        })
      );
    } catch {
      /* Storage may be unavailable; section-render restore remains available. */
    }
  }

  async #restoreState() {
    const state = this.#readState();
    if (!this.#grid || !this.dataset.sectionId) return;

    const pageParam = new URL(window.location.href).searchParams.get('page');
    const parsedPage = Number(pageParam);
    const hasValidPageParam =
      pageParam === null || (Number.isInteger(parsedPage) && parsedPage > 0);
    const requestedPage = hasValidPageParam && pageParam !== null ? parsedPage : 1;
    const pageSize = Number(this.dataset.pageSize || 0);
    const totalItems = Number(this.#grid.dataset.totalItems || 0);
    const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(totalItems / pageSize)) : 1;
    const shouldRestoreScroll = this.#shouldRestoreScroll(state);
    const requestedPageInvalid = !hasValidPageParam || requestedPage > totalPages;
    const savedPageSize = Number(state?.pageSize || 0);
    const pageSizeChanged = savedPageSize > 0 && pageSize > 0 && savedPageSize !== pageSize;
    let loadedPages;

    if (requestedPageInvalid) {
      this.#clearState();
      this.#redirectToFirstPage();
      return;
    }

    if (shouldRestoreScroll) this.#takeOverScrollRestoration();

    if (pageSizeChanged && shouldRestoreScroll) {
      loadedPages = Math.max(1, Math.ceil(Number(state?.loaded || 0) / pageSize));
    } else if (requestedPage > 1) {
      loadedPages = requestedPage;
    } else if (shouldRestoreScroll) {
      loadedPages = Number(state?.loadedPages || 1);
    } else {
      loadedPages = 1;
    }

    loadedPages = Math.min(Math.max(1, loadedPages), totalPages);

    const pageWasNormalized = requestedPage !== loadedPages;

    if (pageWasNormalized) {
      this.#replaceHistoryPage(loadedPages);
    }

    if (loadedPages <= 1 && requestedPage === 1) {
      if (pageSizeChanged) this.#saveState({ includeScroll: false });
      if (shouldRestoreScroll) this.#restoreScroll(state);
      return;
    }

    if (shouldRestoreScroll && this.#restoreSnapshot(state, loadedPages, totalItems)) {
      this.#restoreScroll(state);
      return;
    }

    const canReuseRequestedPage = requestedPage === loadedPages && this.#grid.children.length > 0;
    const currentPageItems = canReuseRequestedPage ? Array.from(this.#grid.children) : [];
    const pagesToFetch = Array.from(
      { length: loadedPages - (canReuseRequestedPage ? 1 : 0) },
      (_, index) => index + 1
    );

    this.#setRestoring(true);
    this.#abortController = new AbortController();

    try {
      const documents = await this.#fetchPages(pagesToFetch);
      const fetchedGrids = documents.map((doc) => doc.querySelector(this.dataset.gridSelector));

      if (!fetchedGrids.length || fetchedGrids.some((grid) => !grid)) {
        throw new Error('A restored lookbook page did not contain its expected grid.');
      }

      this.#grid.replaceChildren(...Array.from(fetchedGrids[0].children));

      fetchedGrids.slice(1).forEach((nextGrid) => {
        this.#grid.append(...Array.from(nextGrid.children));
      });

      if (canReuseRequestedPage) {
        this.#grid.append(...currentPageItems);
      } else {
        const latestPaginator =
          documents[documents.length - 1]?.querySelector('load-more-paginator');
        const currentPaginator = this.querySelector('load-more-paginator');
        if (latestPaginator && currentPaginator) {
          currentPaginator.replaceWith(latestPaginator);
        } else if (latestPaginator) {
          this.querySelector('.lookbook__pagination')?.replaceChildren(latestPaginator);
        } else if (!latestPaginator && currentPaginator) {
          currentPaginator.remove();
        }
      }

      this.#saveState({ includeScroll: false });
      this.#showRestoredContent();

      if (shouldRestoreScroll) this.#restoreScroll(state);
    } catch (error) {
      if (error.name === 'AbortError') {
        this.#releaseScrollRestoration();
        return;
      }

      this.#clearState();
      this.#releaseScrollRestoration();
      if (pageWasNormalized) window.location.replace(window.location.href);
    } finally {
      this.#setRestoring(false);
    }
  }

  #restoreSnapshot(state, currentPage, totalItems) {
    if (!state?.gridHtml || Number(state.loadedPages) !== currentPage) {
      return false;
    }

    const pageSize = Number(this.dataset.pageSize || 0);
    if (state.pageSize && pageSize && Number(state.pageSize) !== pageSize) {
      return false;
    }

    const savedTotal = Number(state.totalItems || state.paginator?.total || 0);
    if (savedTotal !== totalItems) {
      return false;
    }

    this.#grid.innerHTML = state.gridHtml;
    this.#showRestoredContent();

    const paginator = this.querySelector('load-more-paginator');
    if (paginator && state.paginator) {
      paginator.dataset.loaded = String(state.loaded || this.#grid.children.length || 0);
      paginator.dataset.total = state.paginator.total || paginator.dataset.total || '';
      paginator.dataset.nextUrl = state.paginator.nextUrl || '';

      const counter = paginator.querySelector('.LoadMore--Counter');
      if (counter && paginator.dataset.total) {
        counter.textContent = `${paginator.dataset.loaded}/${paginator.dataset.total}`;
      }

      if (state.paginator.exhausted || !paginator.dataset.nextUrl) {
        paginator.setAttribute('data-exhausted', 'true');
        paginator
          .querySelector('.LoadMore--Button, .collection-grid__load-more-btn')
          ?.setAttribute('disabled', '');
      } else {
        paginator.removeAttribute('data-exhausted');
        paginator
          .querySelector('.LoadMore--Button, .collection-grid__load-more-btn')
          ?.removeAttribute('disabled');
      }
    }

    return true;
  }

  #getSnapshotGridHtml() {
    if (!this.#grid) {
      return '';
    }

    const clone = this.#grid.cloneNode(true);
    clone.querySelectorAll('.animatedContent[data-loaded]').forEach((node) => {
      node.removeAttribute('data-loaded');
    });

    return clone.innerHTML;
  }

  #showRestoredContent() {
    if (!this.#grid) return;
    const cells = Array.from(this.#grid.querySelectorAll('.animatedContent'));

    cells.forEach((node) => {
      node.setAttribute('data-loaded', 'true');
      node.style.opacity = '1';
    });
  }

  #setRestoring(isRestoring) {
    const paginator = this.querySelector('load-more-paginator');
    const button = paginator?.querySelector('.LoadMore--Button, .collection-grid__load-more-btn');

    this.toggleAttribute('data-restoring', isRestoring);
    this.#grid?.setAttribute('aria-busy', String(isRestoring));

    if (isRestoring) {
      button?.setAttribute('disabled', '');
    } else if (paginator && !paginator.hasAttribute('data-exhausted')) {
      button?.removeAttribute('disabled');
    }
  }

  async #fetchPages(pages) {
    const documents = [];

    for (let index = 0; index < pages.length; index += RESTORE_CONCURRENCY) {
      const batch = pages.slice(index, index + RESTORE_CONCURRENCY);
      documents.push(...(await Promise.all(batch.map((page) => this.#fetchPage(page)))));
    }

    return documents;
  }

  async #fetchPage(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);
    url.searchParams.set('section_id', this.dataset.sectionId);

    const response = await fetch(url.toString(), { signal: this.#abortController.signal });
    if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  #replaceHistoryPage(page) {
    if (!window.history?.replaceState) return;

    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');

    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    window.history.replaceState(window.history.state, '', url.toString());
  }

  #redirectToFirstPage() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    window.location.replace(url.toString());
  }

  #takeOverScrollRestoration() {
    if (!('scrollRestoration' in window.history)) return;

    this.#previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
  }

  #restoreScroll(state) {
    const scrollY = Number(state?.scrollY || 0);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
        this.#releaseScrollRestoration();
      });
    });
  }

  #releaseScrollRestoration() {
    if (this.#previousScrollRestoration === null) return;

    window.history.scrollRestoration = this.#previousScrollRestoration;
    this.#previousScrollRestoration = null;
  }

  #readState() {
    try {
      return JSON.parse(sessionStorage.getItem(this.#storageKey));
    } catch {
      this.#clearState();
      return null;
    }
  }

  #clearState() {
    try {
      sessionStorage.removeItem(this.#storageKey);
    } catch {
      /* ignore */
    }
  }

  #shouldRestoreScroll(state) {
    if (!state) return false;
    const navigation = performance.getEntriesByType('navigation')?.[0];
    return navigation?.type === 'back_forward' || navigation?.type === 'reload';
  }
}

if (!customElements.get('lookbook-section')) {
  customElements.define('lookbook-section', LookbookSection);
}
